/**
 * Permission-decision helpers for the Content Team feature.
 *
 * Each function takes a `roleStore` (anything exposing `hasPerms(cls, perms)`),
 * the `adminId` of the content-team member whose content is being acted on, and
 * the `currentAdminId` of the logged-in admin.
 *
 * Two independent permission systems are involved:
 *   - Store assignments (the stores shown under a content-team member) are gated
 *     by the `content_team` set: base `content_team.get` plus
 *     `content_team.{show,add,edit,delete}_from_other_admins` when acting on a
 *     DIFFERENT admin's content.
 *   - Notes (inside a store's details) are gated by the `admin_store_note` set
 *     plus `store.{show,add,edit,delete}_other_admin_notes` for other admins.
 *
 * "Other admin" simply means: the member being viewed is not the logged-in admin.
 */

export const isOtherAdmin = (adminId, currentAdminId) => String(adminId) !== String(currentAdminId);

// --- Content team store assignments ----------------------------------------

export const canEnterContentTeam = roleStore => roleStore.hasPerms('content_team', ['get']);

export const canShowStores = (roleStore, adminId, currentAdminId) =>
  !isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('content_team', ['show_from_other_admins']);

export const canAddStore = (roleStore, adminId, currentAdminId) =>
  !isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('content_team', ['add_from_other_admins']);

export const canEditStore = (roleStore, adminId, currentAdminId) =>
  !isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('content_team', ['edit_from_other_admins']);

export const canRemoveStore = (roleStore, adminId, currentAdminId) =>
  !isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('content_team', ['delete_from_other_admins']);

// Can the logged-in admin reach the content-team store details page at all?
export const canOpenContentTeamStore = (roleStore, adminId, currentAdminId) =>
  canEnterContentTeam(roleStore) && canShowStores(roleStore, adminId, currentAdminId);

// --- Store notes ------------------------------------------------------------

export const canViewNotes = (roleStore, adminId, currentAdminId) =>
  roleStore.hasPerms('admin_store_note', ['list', 'get']) &&
  (!isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('store', ['show_other_admin_notes']));

export const canAddNote = (roleStore, adminId, currentAdminId) =>
  roleStore.hasPerms('admin_store_note', ['add']) &&
  (!isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('store', ['add_other_admin_notes']));

export const canEditNote = (roleStore, adminId, currentAdminId) =>
  roleStore.hasPerms('admin_store_note', ['update']) &&
  (!isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('store', ['edit_other_admin_notes']));

export const canDeleteNote = (roleStore, adminId, currentAdminId) =>
  roleStore.hasPerms('admin_store_note', ['delete']) &&
  (!isOtherAdmin(adminId, currentAdminId) || roleStore.hasPerms('store', ['delete_other_admin_notes']));
